import React, { ElementType, forwardRef, InputHTMLAttributes, Ref } from 'react'

import CustomSelect from '@/components/base/CustomSelect'
import Typography from '@/components/base/Typography'

import SVGIcon from './SVGIcon'
import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      resetKey?: string
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
    }
  >

const FormControl = forwardRef(
  (
    {
      id,
      type,
      label,
      checked,
      choices,
      placeholder,
      className,
      children,
      error,
      ...props
    }: FormControlProps,
    ref: Ref<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

    return (
      <div
        className={cn(className, {
          hidden: type === 'hidden'
        })}
      >
        {type === 'consent' ? (
          <label
            className={cn([
              'relative flex items-start space-x-[1.6rem]',
              {
                'pointer-events-none opacity-40': props.disabled
              }
            ])}
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              className={cn(['h-[2.4rem] w-[2.4rem] opacity-0 [&:checked+svg]:opacity-100'])}
              {...props}
            />
            <SVGIcon
              symbol="check-icon"
              className="absolute left-[0.4rem] top-[0.4rem] !m-0 h-[1.6rem] w-[1.6rem] fill-text-primary-dark opacity-0"
            />
            <div
              className={cn([
                'absolute -top-0 left-0 !m-0 h-[2.4rem] w-[2.4rem] shrink-0 cursor-pointer rounded-radius-minimal border border-text-primary-dark/20',
                {
                  'border-error': error
                }
              ])}
            />
            <Typography
              variant="small"
              className={cn([
                'mt-[0.4rem] max-w-[90%] text-text-secondary-dark',
                '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
                '[&>a:hover]:text-text-primary-dark [&>a]:underline',
                '[&>a]:transition-colors [&>a]:duration-300 [&>a]:ease-out-cubic',
                {
                  '!text-error': error
                }
              ])}
            >
              {label}
            </Typography>
          </label>
        ) : type === 'select' ? (
          <CustomSelect
            ref={ref as Ref<HTMLButtonElement>}
            options={choices?.map((choice) => ({ value: choice.value, text: choice.text })) || []}
            placeholder={placeholder}
            onChange={props.onChange}
            onReset={() => props.onChange?.(null)}
            error={error}
            disabled={props.disabled}
            resetKey={props.resetKey}
          />
        ) : (
          <Component
            id={id}
            type={type}
            placeholder={placeholder}
            className={cn([
              'flex w-full appearance-none rounded-[0.4rem] bg-transparent px-spacing-xs py-[0.9rem] font-roboto text-regular outline-none',
              'border border-text-primary-dark/20 text-text-primary-dark placeholder:text-text-primary-dark',
              'focus:border-text-primary-dark/80 focus:ring-2 focus:ring-text-primary-dark/30',
              {
                'min-h-[15rem]': type === 'textarea',
                'border-error focus:ring-error/50': error
              },
              {
                'pointer-events-none opacity-40': props.disabled
              }
            ])}
            ref={type !== 'consent' && type !== 'select' ? ref : undefined}
            {...props}
          />
        )}
        {children}
      </div>
    )
  }
)

FormControl.displayName = 'FormControl'

export default FormControl
